import { FunctionComponent } from 'react';
import { Badge, Box, Flex, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { TeamLite } from './types';
import { SelectedTeamRow } from './SelectedTeamRow';

const PREFIX = 'pages.application_season.components.season_step3_teams';

type SelectedTeamsPanelProps = {
  teams: TeamLite[];
  /** Mapa team_uuid → label do grupo onde está alocado (vazio se "sem grupo"). */
  groupLabelByTeamUuid: Map<string, string>;
  /** Habilita a badge de grupo nas linhas (true em HYBRID). */
  showGroupBadge?: boolean;
  isReadOnly?: boolean;
  onRemove: (teamUuid: string) => void;
};

export const SelectedTeamsPanel: FunctionComponent<SelectedTeamsPanelProps> = ({
  teams,
  groupLabelByTeamUuid,
  showGroupBadge,
  isReadOnly,
  onRemove,
}) => {
  const { t } = useTranslation();

  return (
    <Flex direction="column" gap={2} minW={0}>
      <Flex align="center" justify="space-between" gap={2}>
        <Flex align="center" gap={2} color="gray.500">
          <ChakraFeatherIcon name="list" boxSize="14px" />
          <Text
            fontSize="12px"
            fontWeight="semibold"
            letterSpacing="wide"
            textTransform="uppercase"
          >
            {t(`${PREFIX}.selected.title`)}
          </Text>
        </Flex>
        <Badge variant="subtle" colorScheme="gray" borderRadius="full" px={2}>
          {teams.length}
        </Badge>
      </Flex>

      <Box
        borderWidth="1px"
        borderColor="gray.200"
        borderRadius="md"
        minH="340px"
        maxH="340px"
        overflowY="auto"
      >
        {teams.length === 0 ? (
          <Flex
            direction="column"
            align="center"
            justify="center"
            gap={2}
            py={10}
            color="gray.500"
          >
            <ChakraFeatherIcon name="users" boxSize="22px" />
            <Text fontSize="14px" fontWeight="medium">
              {t(`${PREFIX}.selected.empty_title`)}
            </Text>
            <Text fontSize="12px" textAlign="center" px={4}>
              {t(`${PREFIX}.selected.empty_help`)}
            </Text>
          </Flex>
        ) : (
          <Flex direction="column" p={1} gap={1}>
            {teams.map((team, index) => (
              <SelectedTeamRow
                key={team.uuid}
                index={index}
                name={team.name}
                acronym={team.acronym}
                logoPath={team.logo_path}
                showGroupBadge={showGroupBadge}
                groupLabel={groupLabelByTeamUuid.get(team.uuid)}
                isReadOnly={isReadOnly}
                onRemove={() => onRemove(team.uuid)}
              />
            ))}
          </Flex>
        )}
      </Box>
    </Flex>
  );
};
